/**
 * Video-telestrator on-disk schema.
 *
 * Extends the canonical `SharedShape` model in `./shared-shape.ts`
 * with surface-specific overlay objects (spotlight, dim, formation)
 * and exposes the I/O adapter that translates between the canonical
 * in-memory shape (`kind`, `strokeWidth`) and the legacy on-disk
 * shape stored in `coaching_notes.drawing` /
 * `coaching_clips.drawing` (`type`, `width`).
 *
 * Two versions on disk:
 *   - v2 — the current shape: `{ version: 2, objects: [...] }`. The
 *     `objects[]` array carries shared shape kinds (arrow/line/
 *     freehand/label/zone) AND telestrator-only kinds (spotlight,
 *     dim, formation).
 *   - v1 — legacy: `{ strokes: [{color, width, points}, ...] }`.
 *     Auto-migrated on load to a v2 doc whose `objects[]` consists
 *     entirely of `freehand` shapes.
 *
 * The backend stores this blob as opaque JSON — there is no Pydantic
 * model on the server side and no `_validate_*` for it. All shape
 * validation happens here, on the client, BEFORE save. (Privacy
 * boundary: the renderer must tolerate corrupted blobs gracefully —
 * the validator strips invalid objects rather than crashing.)
 *
 * Source-of-truth references in legacy code:
 *   - js/coaching/review.js:1077–1598  paint logic + schema
 *   - js/coaching/feedback-player.js:454–650  playback
 */

import { clamp01, type Point } from "./coords";
import {
  ALL_VALID_COLORS,
  LEGACY_DEFAULT_COLOR,
  SHARED_STROKE_WIDTH_DEFAULT,
  SHARED_STROKE_WIDTH_MAX,
  SHARED_STROKE_WIDTH_MIN,
  type SharedColor,
  type SharedShape,
  type ValidationResult,
  isSharedColor,
  isSharedStrokeWidth,
  isValidUnit,
  normalizeSharedColor,
  normalizeSharedStrokeWidth,
  validateSharedShape,
} from "./shared-shape";

// ---------------------------------------------------------------------------
// Telestrator-only objects
// ---------------------------------------------------------------------------

/** Spotlight — full-frame darkening overlay with a circular "hole"
 * at the center of the (x, y, w, h) rect. Rendered via
 * `globalCompositeOperation: "destination-out"` in the legacy
 * painter (review.js:1317-1326). The Konva equivalent is a custom
 * `sceneFunc` on a Group that draws a black rect then erases an
 * ellipse hole. */
export type SpotlightObject = {
  kind: "spotlight";
  id?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color?: SharedColor;
  strokeWidth?: number;
};

/** Circle — a rect-bounded ellipse the coach drags out, stroked
 * (not filled). Same `(x, y, w, h)` bounds + drag + move machine as
 * `zone` and `spotlight`, but it's a telestrator-only kind — the
 * tactical board never had it. Ports legacy `js/coaching/review.js`
 * `circle` (key `C`, render at :1312-1329, bounds at :1416). Drawn
 * as a Konva ellipse inscribed in the bounds. */
export type CircleObject = {
  kind: "circle";
  id?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color?: SharedColor;
  strokeWidth?: number;
};

/** Dim — full-frame translucent black overlay. The only "drawing"
 * tool that takes no spatial input — one click anywhere places it.
 * Default opacity is 0.45 (legacy review.js:1283-1285). */
export type DimObject = {
  kind: "dim";
  id?: string;
  /** [0, 1] inclusive. Default 0.45. */
  opacity: number;
};

/** Formation — coach-drawn lineup overlay. Anchors are the player
 * positions in unit coords; `hull_points` is the convex hull computed
 * at save time (Andrew's monotone chain in review.js:1580-1598). The
 * hull is not recomputed on render. */
export type FormationAnchor = {
  x: number;
  y: number;
  player_id?: string;
  label?: string;
};

export type FormationObject = {
  kind: "formation";
  id?: string;
  anchors: FormationAnchor[];
  hull_points: Point[];
  color?: SharedColor;
  strokeWidth?: number;
};

/** Discriminated union of the 4 telestrator-only kinds. */
export type TelestratorOnlyObject =
  | SpotlightObject
  | CircleObject
  | DimObject
  | FormationObject;

/** Anything renderable on the telestrator: a shared shape OR a
 * telestrator-only overlay. */
export type TelestratorObject = SharedShape | TelestratorOnlyObject;

/** The persisted document shape (v2). */
export type DrawingV2 = {
  version: 2;
  objects: TelestratorObject[];
};

/** Legacy v1 — stroke list. Auto-migrated on load; never emitted
 * on save. */
export type DrawingV1 = {
  strokes: { color: string; width: number; points: Point[] }[];
};

/** Anything we might receive as a saved drawing. */
export type DrawingAnyVersion = DrawingV1 | DrawingV2;

// ---------------------------------------------------------------------------
// Constants (mostly inherited; telestrator-specific defaults below)
// ---------------------------------------------------------------------------

export const DEFAULT_DIM_OPACITY = 0.45;
export const MAX_FORMATION_ANCHORS = 16;
export const MIN_FORMATION_ANCHORS = 3;
// A formation hull is the convex hull of its anchors, so it can never
// have more vertices than there are anchors. Mirror the anchor cap
// (and the backend's `validate_drawing_payload`, which rejects
// hull_points > 16) — the old value of 32 was dead-permissive.
export const MAX_FORMATION_HULL_POINTS = 16;
// Telestrator label text is capped tighter than the shared
// `SHARED_MAX_LABEL_LENGTH` (80): the backend's drawing validator
// (`models.py` — "label drawing text must be 1 to 40 characters")
// rejects anything longer. The tactical board keeps the 80-char
// shared cap; only the over-video telestrator label is 40.
export const MAX_TELESTRATOR_LABEL_LENGTH = 40;
// Max objects in one drawing — mirrors the backend's
// `validate_drawing_payload` ("drawing objects must be a list of
// 120 or fewer"). Enforced at commit time so a drawing can't grow
// past what a save would accept.
export const MAX_DRAWING_OBJECTS = 120;

/** Tool kinds the telestrator exposes (board's shared tools +
 * surface-specific overlays). Does NOT include `player`/`ball` —
 * those are tactical-board-only tokens. */
export const TELESTRATOR_TOOL_KINDS = [
  "select",
  "arrow",
  "line",
  "zone",
  "circle",
  "label",
  "freehand",
  "spotlight",
  "dim",
  "formation",
] as const;
export type TelestratorToolKind = (typeof TELESTRATOR_TOOL_KINDS)[number];

/** The 4 surface-specific kinds the telestrator validates. Shared
 * kinds go through `validateSharedShape`. */
export const TELESTRATOR_ONLY_KINDS = [
  "spotlight",
  "circle",
  "dim",
  "formation",
] as const;
export type TelestratorOnlyKind = (typeof TELESTRATOR_ONLY_KINDS)[number];

export function isTelestratorOnlyKind(
  value: unknown,
): value is TelestratorOnlyKind {
  return (
    typeof value === "string" &&
    (TELESTRATOR_ONLY_KINDS as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Validators for telestrator-only objects
// ---------------------------------------------------------------------------

function readMeta(
  input: Record<string, unknown>,
): { color?: SharedColor; strokeWidth?: number; id?: string } {
  const out: { color?: SharedColor; strokeWidth?: number; id?: string } = {};
  const color = normalizeSharedColor(input.color);
  if (color !== undefined) out.color = color;
  const strokeWidth = normalizeSharedStrokeWidth(input.strokeWidth);
  if (strokeWidth !== undefined) out.strokeWidth = strokeWidth;
  if (typeof input.id === "string" && input.id) out.id = input.id;
  return out;
}

export function validateSpotlight(
  input: unknown,
  index = 0,
): ValidationResult<SpotlightObject> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, reason: `objects[${index}] spotlight must be an object` };
  }
  const s = input as Record<string, unknown>;
  if (s.kind !== "spotlight") {
    return { ok: false, reason: `objects[${index}].kind must be spotlight` };
  }
  for (const k of ["x", "y", "w", "h"] as const) {
    if (!isValidUnit(s[k])) {
      return {
        ok: false,
        reason: `objects[${index}].${k} must be in [0, 1]`,
      };
    }
  }
  const x = s.x as number;
  const y = s.y as number;
  const w = s.w as number;
  const h = s.h as number;
  if (w <= 0 || h <= 0) {
    return {
      ok: false,
      reason: `objects[${index}] spotlight w/h must be > 0`,
    };
  }
  if (x + w > 1.0001 || y + h > 1.0001) {
    return {
      ok: false,
      reason: `objects[${index}] spotlight extends past frame bounds`,
    };
  }
  return { ok: true, value: { kind: "spotlight", x, y, w, h, ...readMeta(s) } };
}

export function validateCircle(
  input: unknown,
  index = 0,
): ValidationResult<CircleObject> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, reason: `objects[${index}] circle must be an object` };
  }
  const s = input as Record<string, unknown>;
  if (s.kind !== "circle") {
    return { ok: false, reason: `objects[${index}].kind must be circle` };
  }
  for (const k of ["x", "y", "w", "h"] as const) {
    if (!isValidUnit(s[k])) {
      return { ok: false, reason: `objects[${index}].${k} must be in [0, 1]` };
    }
  }
  const x = s.x as number;
  const y = s.y as number;
  const w = s.w as number;
  const h = s.h as number;
  if (w <= 0 || h <= 0) {
    return { ok: false, reason: `objects[${index}] circle w/h must be > 0` };
  }
  if (x + w > 1.0001 || y + h > 1.0001) {
    return {
      ok: false,
      reason: `objects[${index}] circle extends past frame bounds`,
    };
  }
  return { ok: true, value: { kind: "circle", x, y, w, h, ...readMeta(s) } };
}

export function validateDim(
  input: unknown,
  index = 0,
): ValidationResult<DimObject> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, reason: `objects[${index}] dim must be an object` };
  }
  const s = input as Record<string, unknown>;
  if (s.kind !== "dim") {
    return { ok: false, reason: `objects[${index}].kind must be dim` };
  }
  // Default opacity if missing / invalid.
  let opacity = DEFAULT_DIM_OPACITY;
  if (s.opacity !== undefined && s.opacity !== null) {
    if (
      typeof s.opacity !== "number" ||
      !Number.isFinite(s.opacity) ||
      s.opacity < 0 ||
      s.opacity > 1
    ) {
      return {
        ok: false,
        reason: `objects[${index}] dim opacity must be in [0, 1]`,
      };
    }
    opacity = s.opacity;
  }
  const out: DimObject = { kind: "dim", opacity };
  if (typeof s.id === "string" && s.id) out.id = s.id;
  return { ok: true, value: out };
}

export function validateFormation(
  input: unknown,
  index = 0,
): ValidationResult<FormationObject> {
  if (typeof input !== "object" || input === null) {
    return {
      ok: false,
      reason: `objects[${index}] formation must be an object`,
    };
  }
  const s = input as Record<string, unknown>;
  if (s.kind !== "formation") {
    return { ok: false, reason: `objects[${index}].kind must be formation` };
  }
  if (!Array.isArray(s.anchors)) {
    return {
      ok: false,
      reason: `objects[${index}] formation anchors must be a list`,
    };
  }
  if (s.anchors.length < MIN_FORMATION_ANCHORS) {
    return {
      ok: false,
      reason: `objects[${index}] formation needs at least ${MIN_FORMATION_ANCHORS} anchors`,
    };
  }
  if (s.anchors.length > MAX_FORMATION_ANCHORS) {
    return {
      ok: false,
      reason: `objects[${index}] formation exceeds max ${MAX_FORMATION_ANCHORS} anchors`,
    };
  }
  const anchors: FormationAnchor[] = [];
  for (let i = 0; i < s.anchors.length; i++) {
    const a = s.anchors[i];
    if (typeof a !== "object" || a === null) {
      return {
        ok: false,
        reason: `objects[${index}].anchors[${i}] must be an object`,
      };
    }
    const ar = a as Record<string, unknown>;
    if (!isValidUnit(ar.x) || !isValidUnit(ar.y)) {
      return {
        ok: false,
        reason: `objects[${index}].anchors[${i}] x/y must be in [0, 1]`,
      };
    }
    const anchor: FormationAnchor = { x: ar.x as number, y: ar.y as number };
    if (typeof ar.player_id === "string" && ar.player_id) {
      anchor.player_id = ar.player_id;
    }
    if (typeof ar.label === "string" && ar.label) {
      anchor.label = ar.label;
    }
    anchors.push(anchor);
  }
  if (!Array.isArray(s.hull_points)) {
    return {
      ok: false,
      reason: `objects[${index}] formation hull_points must be a list`,
    };
  }
  if (s.hull_points.length > MAX_FORMATION_HULL_POINTS) {
    return {
      ok: false,
      reason: `objects[${index}] formation hull exceeds max ${MAX_FORMATION_HULL_POINTS} points`,
    };
  }
  const hull: Point[] = [];
  for (let i = 0; i < s.hull_points.length; i++) {
    const p = s.hull_points[i];
    if (typeof p !== "object" || p === null) {
      return {
        ok: false,
        reason: `objects[${index}].hull_points[${i}] must be an object`,
      };
    }
    const pr = p as Record<string, unknown>;
    if (!isValidUnit(pr.x) || !isValidUnit(pr.y)) {
      return {
        ok: false,
        reason: `objects[${index}].hull_points[${i}] x/y must be in [0, 1]`,
      };
    }
    hull.push({ x: pr.x as number, y: pr.y as number });
  }
  return {
    ok: true,
    value: {
      kind: "formation",
      anchors,
      hull_points: hull,
      ...readMeta(s),
    },
  };
}

/** Validate any single telestrator object. Dispatches on `kind`. */
export function validateTelestratorObject(
  input: unknown,
  index = 0,
): ValidationResult<TelestratorObject> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, reason: `objects[${index}] must be an object` };
  }
  const kind = (input as Record<string, unknown>).kind;
  switch (kind) {
    case "spotlight":
      return validateSpotlight(input, index);
    case "circle":
      return validateCircle(input, index);
    case "dim":
      return validateDim(input, index);
    case "formation":
      return validateFormation(input, index);
    default: {
      const result = validateSharedShape(input, index);
      // `validateSharedShape` caps label text at the shared 80-char
      // limit; the telestrator backend rejects > 40. Re-check here so
      // an over-long label can't survive a `loadDrawing` round-trip
      // and then 422 on the next save.
      if (
        result.ok &&
        result.value.kind === "label" &&
        result.value.text.length > MAX_TELESTRATOR_LABEL_LENGTH
      ) {
        return {
          ok: false,
          reason: `objects[${index}] label text exceeds max length ${MAX_TELESTRATOR_LABEL_LENGTH}`,
        };
      }
      return result;
    }
  }
}

// ---------------------------------------------------------------------------
// On-disk adapter
// ---------------------------------------------------------------------------
//
// The telestrator's persisted blob uses `type` (not `kind`) and `width`
// (not `strokeWidth`) — a different convention from the tactical
// board. Adapter functions translate at the I/O boundary so
// components, hooks, and the editor store never see the on-disk form.

/** On-disk shape: replace `kind` with `type`, `strokeWidth` with
 * `width`. Other fields are identity. Surface-specific kinds keep
 * their kind value (dim/spotlight/formation are still
 * discriminator strings on disk). */
type OnDiskKey = "type" | "width";
type OnDiskCommon = Record<string, unknown> & Partial<Record<OnDiskKey, unknown>>;

/** Convert a single in-memory object to the on-disk shape. Used by
 * `toOnDisk` and exported for tests. */
export function objectToOnDisk(obj: TelestratorObject): OnDiskCommon {
  const { kind, ...rest } = obj as TelestratorObject & { kind: string };
  const out: OnDiskCommon = { ...(rest as Record<string, unknown>), type: kind };
  if ("strokeWidth" in out) {
    out.width = out.strokeWidth;
    delete out.strokeWidth;
  }
  // Legacy painter strips IDs on save to keep the blob small
  // (review.js:79). Mirror that here so we don't bloat persisted
  // drawings — IDs are generated fresh on every load anyway.
  if ("id" in out) delete out.id;
  return out;
}

/** Convert a single on-disk object back to the canonical in-memory
 * shape. Used by `fromOnDisk` and exported for tests. */
export function objectFromOnDisk(raw: unknown): TelestratorObject | null {
  if (typeof raw !== "object" || raw === null) return null;
  const source = raw as Record<string, unknown>;
  const copy: Record<string, unknown> = { ...source };
  // `type` → `kind`
  if (copy.type !== undefined && copy.kind === undefined) {
    copy.kind = copy.type;
    delete copy.type;
  }
  // `width` → `strokeWidth`
  if (copy.width !== undefined && copy.strokeWidth === undefined) {
    copy.strokeWidth = copy.width;
    delete copy.width;
  }
  const r = validateTelestratorObject(copy);
  return r.ok ? r.value : null;
}

/** Migrate a v1 drawing to v2. Each v1 stroke becomes a v2 freehand
 * shape. Strokes with invalid points are dropped silently. */
export function migrateV1ToV2(v1: DrawingV1): DrawingV2 {
  const objects: TelestratorObject[] = [];
  for (const raw of v1.strokes ?? []) {
    if (typeof raw !== "object" || raw === null) continue;
    const stroke = raw as Record<string, unknown>;
    if (!Array.isArray(stroke.points)) continue;
    const points: Point[] = [];
    for (const p of stroke.points) {
      if (typeof p !== "object" || p === null) continue;
      const pp = p as Record<string, unknown>;
      if (!isValidUnit(pp.x) || !isValidUnit(pp.y)) continue;
      points.push({ x: pp.x as number, y: pp.y as number });
    }
    if (points.length < 2) continue;
    const color = isSharedColor(stroke.color) ? stroke.color : undefined;
    const strokeWidth = isSharedStrokeWidth(stroke.width)
      ? Math.round(stroke.width)
      : undefined;
    const shape: SharedShape = {
      kind: "freehand",
      points,
      ...(color !== undefined ? { color } : {}),
      ...(strokeWidth !== undefined ? { strokeWidth } : {}),
    };
    objects.push(shape);
  }
  return { version: 2, objects };
}

/** Detect whether a raw blob looks like the v1 stroke-list shape. */
export function isDrawingV1(input: unknown): input is DrawingV1 {
  if (typeof input !== "object" || input === null) return false;
  const s = input as Record<string, unknown>;
  return Array.isArray(s.strokes) && (s.version === undefined || s.version === 1);
}

/** Load a drawing blob from disk into the canonical in-memory v2
 * shape. Handles:
 *   - null / undefined / empty object → empty v2 doc
 *   - v1 stroke list → migrate to v2
 *   - v2 with `objects` → validate each object (drop invalid silently
 *     to keep the renderer crash-free)
 */
export function loadDrawing(input: unknown): DrawingV2 {
  if (input === null || input === undefined) return emptyDrawing();
  if (typeof input !== "object") return emptyDrawing();
  if (isDrawingV1(input)) return migrateV1ToV2(input);
  const s = input as Record<string, unknown>;
  if (!Array.isArray(s.objects)) return emptyDrawing();
  const objects: TelestratorObject[] = [];
  for (let i = 0; i < s.objects.length; i++) {
    const obj = objectFromOnDisk(s.objects[i]);
    if (obj !== null) objects.push(obj);
  }
  return { version: 2, objects };
}

/** Serialize the canonical in-memory drawing back to disk. Strips IDs
 * per the legacy convention and renames fields. */
export function saveDrawing(drawing: DrawingV2): {
  version: 2;
  objects: OnDiskCommon[];
} {
  return {
    version: 2,
    objects: drawing.objects.map(objectToOnDisk),
  };
}

export function emptyDrawing(): DrawingV2 {
  return { version: 2, objects: [] };
}

/** Clamp the coords of any telestrator object into [0, 1]. */
export function clampTelestratorObject(obj: TelestratorObject): TelestratorObject {
  switch (obj.kind) {
    case "spotlight":
    case "circle": {
      const x = clamp01(obj.x);
      const y = clamp01(obj.y);
      const w = Math.min(Math.max(obj.w, 0.001), 1 - x);
      const h = Math.min(Math.max(obj.h, 0.001), 1 - y);
      return { ...obj, x, y, w, h };
    }
    case "dim":
      return obj;
    case "formation":
      return {
        ...obj,
        anchors: obj.anchors.map((a) => ({
          ...a,
          x: clamp01(a.x),
          y: clamp01(a.y),
        })),
        hull_points: obj.hull_points.map((p) => ({
          x: clamp01(p.x),
          y: clamp01(p.y),
        })),
      };
    default: {
      // Shared shapes — delegate to shared clamp via the validator
      // (re-importing clampSharedShape here would create a circular
      // import; the validator itself returns a clean copy).
      // Use individual switch cases:
      const shape = obj as SharedShape;
      switch (shape.kind) {
        case "arrow":
        case "line":
          return {
            ...shape,
            x1: clamp01(shape.x1),
            y1: clamp01(shape.y1),
            x2: clamp01(shape.x2),
            y2: clamp01(shape.y2),
          };
        case "zone": {
          const x = clamp01(shape.x);
          const y = clamp01(shape.y);
          return { ...shape, x, y };
        }
        case "label":
          return {
            ...shape,
            x: clamp01(shape.x),
            y: clamp01(shape.y),
          };
        case "freehand":
          return {
            ...shape,
            points: shape.points.map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) })),
          };
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Convex hull (Andrew's monotone chain) for the Formation tool
// ---------------------------------------------------------------------------
//
// Computed at FORMATION-SAVE time, persisted as `hull_points`, and
// NOT recomputed on render. Mirrors the legacy painter's behavior at
// review.js:1580–1598.

/** Compute the convex hull of a set of points using Andrew's
 * monotone chain. Returns the hull vertices in counter-clockwise
 * order. For < 3 points, returns the input unchanged. */
export function convexHull(points: Point[]): Point[] {
  if (points.length < 3) return [...points];
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o: Point, a: Point, b: Point) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Point[] = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: Point[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i]!;
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  // Drop the duplicated start/end points and return CCW order.
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

// ---------------------------------------------------------------------------
// Re-exports for convenience
// ---------------------------------------------------------------------------

export {
  ALL_VALID_COLORS as VALID_COLORS,
  LEGACY_DEFAULT_COLOR,
  SHARED_STROKE_WIDTH_DEFAULT as TELESTRATOR_STROKE_WIDTH_DEFAULT,
  SHARED_STROKE_WIDTH_MAX as TELESTRATOR_STROKE_WIDTH_MAX,
  SHARED_STROKE_WIDTH_MIN as TELESTRATOR_STROKE_WIDTH_MIN,
};
export type { SharedColor as TelestratorColor };
